import { ArrowLeftIcon } from 'lucide-react'
import { Icon } from '@/components'
import Text from '@/components/Text'
import { Link } from '@mui/material'
import useZendeskArticle from './useZendeskArticle'
import './styles.scss'

const OPTIWATT_FAQ_URL = 'https://optiwatt.com/faq'

type Props = {
  open: boolean
  onClose: () => void
}

export default function ExplainOptiwattModal({ open, onClose }: Props) {
  const { bodyHtml, isLoading, error } = useZendeskArticle()

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-white w-[900px] max-h-[calc(100vh-128px)] rounded-[20px] p-6 shadow-[0_0_10px_rgba(0,0,0,0.1)] overflow-y-auto flex flex-col gap-6 font-[inter]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col gap-6">
          <button
            onClick={onClose}
            className="flex items-center justify-center size-[32px] rounded-full bg-[#e5e5e5] border-0 cursor-pointer hover:bg-[#d5d5d5] active:scale-95 transition-colors p-0"
          >
            <ArrowLeftIcon size={20} />
          </button>

          <img
            src="/img/logo-color.webp"
            width={140}
            alt="Optiwatt"
            className="block"
          />

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Icon
                name="Users"
                size={20}
                color="purple-500"
                strokeWidth={2.25}
              />
              <span className="text-[14px] font-medium text-[#5d5d5d] leading-5">
                20,000+ people enrolled in programs
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon
                name="Star"
                size={20}
                color="purple-500"
                strokeWidth={2.25}
              />
              <span className="text-[14px] font-medium text-[#5d5d5d] leading-5">
                4.8/5 rating on the{' '}
                <a
                  href="https://apps.apple.com/us/app/optiwatt-local-energy-rewards/id1536047033"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#5d5d5d] !underline"
                >
                  App Store
                </a>
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-[2px] shrink-0 bg-[#e5e5e5]" />

        {/* Zendesk Article Content */}
        <div>
          {isLoading && (
            <div className="flex flex-col gap-4 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-5/6" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-2/3" />
            </div>
          )}
          {error && (
            <div className="text-center py-8">
              <Text variant="body2" className="text-gray-500">
                Unable to load content. Please try again later.
              </Text>
            </div>
          )}
          {bodyHtml && (
            <div
              className="zendesk-article-content"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          )}
        </div>

        {/* Divider */}
        <div className="w-full h-[2px] shrink-0 bg-[#e5e5e5]" />

        {/* Footer */}
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-1">
            <p className="text-[12px] font-medium text-[#5d5d5d] leading-5 m-0">
              Still have questions?
            </p>
            <div className="flex items-center gap-1">
              <Link
                href={OPTIWATT_FAQ_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="!text-[#3768fb] !font-bold !text-[14px] !underline !leading-6"
              >
                Visit FAQ Page
              </Link>
              <Icon name="ExternalLink" size={16} color="#3768fb" />
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full bg-[#af4bfb] hover:bg-[#9a3ae6] text-white font-bold text-[14px] leading-5 p-5 rounded-[20px] transition-colors border-0 cursor-pointer font-[inter]"
          >
            Return to Sign Up
          </button>
        </div>
      </div>
    </div>
  )
}
